import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { batch, useDispatch, useSelector } from '@tmapy/redux';
import { useMessage } from '@tmapy/intl';

import './OrderPublishsetList.scss';

import {
  DangerAlert,
  CheckboxGroup,
  WaitingAlert,
  StickyFooter,
  BtnBar,
  CtaBtn,
  RadioGroup,
  InfoAlert,
  FormItem,
  FormRow,
  SvgArrowRight,
  Checkbox,
  Input,
  Badge,
} from '@tmapy/style-guide';


import { usePublishsetTagsListResource } from '../../openapi/operations/publishsetTagsList';
import { usePublishsetsListResource } from '../../openapi/operations/publishsetsList';
import { OrderTypeEnum } from '../../openapi/types/OrderTypeEnum';
import { OrderSelectionTypesEnum } from '../../openapi/types/OrderSelectionTypesEnum';

import { useCheckbox } from '../../hooks/useCheckbox';
import { OmittedFormItemProps } from '../../types';
import { ORDER_IS_PUBLIC, QUERY_STATE } from '../../constants';
import {
  actionSetAreaType,
  actionSetIsPublic,
  actionSetStateDataValidityDate,
  actionSetChangeDataValidityDateFrom,
  actionSetDataValidity,
  actionSetSelectionType,
  actionSetPrimaryDB,
  actionSetIsRequirementDate,
} from '../actions/newOrder';

import { OrderPublishsetListItem } from './OrderPublishsetListItem';
import {
  isPublicMessages,
  dataValidityTypesMessages,
  newOrderMessages,
  typesMessages,
  orderPublishsetListMessages,
} from './orderMessages';

type Tag = { id: number; url: string; name: string };

export const PUBLISHSET_LIST_FILTER = {
  page_size: 100000,
  state: 'PUBLISHED',
} as const;

export const TAGS_LIST_FILTER = {
  page_size: 100000,
} as const;

type OrderPublishsetListProps = {
  onNextStepClick(): void;
  onChange(clearAreaType: boolean): void;
};

const dateFromDateString = (dateStr: string): Date => {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const firstDayOfCurrentMonth = new Date();
firstDayOfCurrentMonth.setDate(
  new Date(firstDayOfCurrentMonth.getFullYear(), firstDayOfCurrentMonth.getMonth(), 1).getDate(),
);

// Historická data začínají od 1.9.2024 - zadavaci podminka
const historyDataFirstDate = new Date('2024-09-01T00:00:00Z');

export const OrderPublishsetList: React.FC<OrderPublishsetListProps> = ({
  onNextStepClick,
  onChange,
}) => {
  const dispatch = useDispatch();
  const formatMessage = useMessage();
  const customerType = useSelector((state) => state.app.config.customerType);
  const hideTags = useSelector((state) => state.app.config.vydej?.hideTags);

  const {
    order_type,
    order_data_state_valid_at,
    order_data_changes_from,
    selection_type,
    is_public,
    from_primary_db,
  } = useSelector((state) => state.newOrder);

  const dataIsPublicItems = useMemo(() => {
    return (
      Object.keys(ORDER_IS_PUBLIC).map((item) => {
        return {
          value: item,
          label: formatMessage.lookup(isPublicMessages, item) || item,
        };
      }) ?? []
    );
  }, [formatMessage]);

  const dataValidityItems = useMemo(() => {
    const validityItems: OrderTypeEnum[] = Object.keys(OrderTypeEnum).reduce((acc, item) => {
      // odebere polozku platnost zmenova pro typ vydeje ad hoc
      if (selection_type === OrderSelectionTypesEnum.CUSTOM && item === OrderTypeEnum.CHANGE) {
        return acc;
      }
      acc.push(item as OrderTypeEnum);
      return acc;
    }, [] as OrderTypeEnum[]);

    return (
      validityItems.map((item) => {
        return {
          value: item,
          label: formatMessage.lookup(dataValidityTypesMessages, item as OrderTypeEnum) || item,
        };
      }) ?? []
    );
  }, [formatMessage, selection_type]);

  const selectionTypeItems = useMemo(() => {
    return (
      Object.keys(OrderSelectionTypesEnum).map((item) => {
        return {
          value: item,
          label: formatMessage.lookup(typesMessages, item as OrderSelectionTypesEnum) || item,
        };
      }) ?? []
    );
  }, [formatMessage]);

  const handleIsPublicChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(actionSetIsPublic(e.target.value as ORDER_IS_PUBLIC));
      onChange(true);
    },
    [dispatch, onChange],
  );

  const handleSelectionTypeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value as OrderSelectionTypesEnum;

      batch('handleSelectionTypeChange', () => {
        dispatch(actionSetSelectionType(value));
        // reset data validity form element
        dispatch(actionSetDataValidity(OrderTypeEnum.STATE));
        dispatch(actionSetAreaType(''));
      });

      onChange(true);
    },
    [dispatch, onChange],
  );

  const handleDataValidityChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value as OrderTypeEnum;

      batch('handleDataValidityChange', () => {
        dispatch(actionSetDataValidity(value));
        dispatch(actionSetAreaType(''));
      });

      onChange(true);
    },
    [dispatch, onChange],
  );

  const publishsetList = usePublishsetsListResource(
    from_primary_db
      ? { ...PUBLISHSET_LIST_FILTER, from_primary_db: true }
      : {
        ...PUBLISHSET_LIST_FILTER,
        order_type: order_type,
        order_is_public: is_public === ORDER_IS_PUBLIC.TRUE,
        order_selection_type: selection_type,
        only_jvf: !!order_data_state_valid_at,
      },
    true,
  );

  const [filteredTags, setFilteredTags] = useState<Tag[]>(() => []);
  const tagList = usePublishsetTagsListResource(TAGS_LIST_FILTER, true);

  const tags = useMemo(() => {
    if (!tagList.data?.results || hideTags) return [];
    const tags = tagList.data?.results.filter(
      // OpenAPI tvrdi, ze "published_publishsets_count" je string (BE nedokaze vyresit lepe)
      (tag) => (tag.published_publishsets_count as any as number) > 0,
    );
    setFilteredTags(tags.filter((tag) => tag.is_default));
    return tags;
  }, [tagList.data, hideTags]);

  const filteredPublishsets = useMemo(() => {
    return (
      publishsetList.data?.results?.filter((publishet) => {
        if (filteredTags.length === 0) return true;
        return publishet.tags?.some((tag) =>
          filteredTags.some((filterTag) => filterTag.id === tag.id),
        );
      }) ?? []
    );
  }, [filteredTags, publishsetList.data?.results]);

  const countByTagId = useMemo(() => {
    const counts = new Map<number, number>();
    publishsetList.data?.results?.forEach((ps) => {
      ps.tags?.forEach((tag) => {
        counts.set(tag.id, (counts.get(tag.id) ?? 0) + 1);
      });
    });
    return counts;
  }, [publishsetList.data?.results]);

  const renderDataValidityCustomItem = useCallback(
    (item: { label: string; value: string }) => {
      const handleStateDataValidityChange = (
        element: React.ChangeEvent<HTMLInputElement>,
        eventData: { name: string; value: string },
      ) => {
        dispatch(
          actionSetStateDataValidityDate(
            eventData.value ? dateFromDateString(eventData.value) : null,
            element.currentTarget.min ? new Date(element.currentTarget.min) : null,
            element.currentTarget.max ? new Date(element.currentTarget.max) : null,
          ),
        );
      };

      return (
        <div className='sg-a-d-f sg-a-fw-w sg-a-ai-b sg-a-g-1/2'>
          {item.label}
          {item.value === OrderTypeEnum.STATE && (
            <label className='sg-a-d-f sg-a-ai-b sg-a-cg-1/2'>
              <FormattedMessage {...newOrderMessages.dataValidityDate} />
              <div className='dmx-orderPublishSetList--datePicker'>
                <Input
                  type='date'
                  name='order_data_state_valid_at'
                  value={order_data_state_valid_at?.toLocaleDateString('en-CA') ?? ''}
                  isDisabled={order_type !== OrderTypeEnum.STATE || !!from_primary_db}
                  max={new Date().toLocaleDateString('en-CA')}
                  min={historyDataFirstDate.toLocaleDateString('en-CA')}
                  onChange={handleStateDataValidityChange}
                />
              </div>
            </label>
          )}
        </div>
      );
    },
    [order_type, dispatch, order_data_changes_from, order_data_state_valid_at, from_primary_db],
  );

  const fromPrimaryDB = useCheckbox<OmittedFormItemProps>({
    label: formatMessage(newOrderMessages.fromPrimaryDB),
    initialValue: from_primary_db,
    name: 'fromPrimaryDB',
    onChange: useCallback(
      (e: React.ChangeEvent) => {
        batch('fromPrimaryDBChange', () => {
          // @ts-ignore TODO: Refactor tech dept
          dispatch(actionSetPrimaryDB(e.target.checked));
          dispatch(actionSetAreaType(''));
        });
        onChange(true);
      },
      [dispatch, onChange],
    ),
  });

  const renderDataValidityPackageItem = useCallback(
    (item: { label: string; value: string }) => {
      const handleChangeDataValidityFromChange = (
        element: React.ChangeEvent<HTMLInputElement>,
        eventData: { name: string; value: string },
      ) => {
        dispatch(
          actionSetChangeDataValidityDateFrom(
            eventData.value ? dateFromDateString(eventData.value) : null,
            element.currentTarget.min ? new Date(element.currentTarget.min) : null,
            element.currentTarget.max ? new Date(element.currentTarget.max) : null,
          ),
        );
        // promazani validace v summary
        if (eventData.value) dispatch(actionSetIsRequirementDate(false));
      };
      return (
        <div className='sg-a-d-f sg-a-fw-w sg-a-ai-b sg-a-g-1/2'>
          {item.label}
          {item.value === OrderTypeEnum.CHANGE && (
            <div className='sg-a-d-f sg-a-fw-w sg-a-ai-b sg-a-g-1/2'>
              <label className='sg-a-d-f sg-a-ai-b sg-a-cg-1/2'>
                <FormattedMessage {...newOrderMessages.dataValidityDateFrom} />
                <div className='dmx-orderPublishSetList--datePicker'>
                  <Input
                    type='date'
                    name='order_data_changes_from'
                    value={order_data_changes_from?.toLocaleDateString('en-CA') ?? ''}
                    isDisabled={order_type !== OrderTypeEnum.CHANGE || !!from_primary_db}
                    max={new Date().toLocaleDateString('en-CA')}
                    min={firstDayOfCurrentMonth.toLocaleDateString('en-CA')}
                    onChange={handleChangeDataValidityFromChange}
                  />
                </div>
              </label>
            </div>
          )}
        </div>
      );
    },
    [order_data_changes_from, order_type, from_primary_db, dispatch],
  );

  return (
    <>
      <div className='dmx-orderPublishSetList'>
        {tags.length > 0 && (
          <div className='dmx-orderPublishSetList--tags'>
            <p className='sg-u-type-sideNavHeading sg-a-mb-1'>
              <FormattedMessage {...orderPublishsetListMessages.tagsTitle} />
            </p>
            <CheckboxGroup
              items={tags}
              values={filteredTags}
              renderLabel={(item) => {
                const isActive =
                  filteredTags.length === 0 || filteredTags.some((t) => t.id === item.id);
                return (
                  <span className='dmx-orderPublishSetList--tagLabel'>
                    <span className='sg-u-type-sml'>{item.name}</span>
                    {countByTagId.has(item.id) && (
                      <Badge
                        type={isActive ? 'info' : undefined}
                        unsafeClassName={isActive ? undefined : 'dmx-orderPublishSetList--tagBadgeMuted'}
                      >
                        {countByTagId.get(item.id)}
                      </Badge>
                    )}
                  </span>
                );
              }}
              renderValue={(item) => item.id}
              onChange={(_e, eventData) => setFilteredTags(eventData!.values)}
            />
          </div>
        )}
        {customerType === 'dtm' && (
          <div className='dmx-orderPublishSetList--validity sg-u-vs-1 sg-u-bb sg-a-pb-2'>
            {from_primary_db && (
              <InfoAlert>
                <FormattedMessage {...newOrderMessages.fromPrimaryDBInfoText} />
              </InfoAlert>
            )}
            <FormRow>
              <FormItem {...fromPrimaryDB}>
                <Checkbox {...fromPrimaryDB} />
              </FormItem>
            </FormRow>
            <FormRow>
              <FormItem label={formatMessage(newOrderMessages.isPublicLabel)}>
                <RadioGroup
                  isDisabled={!!from_primary_db}
                  direction='row'
                  name='is_public'
                  value={is_public ?? dataIsPublicItems[0]?.value}
                  items={dataIsPublicItems}
                  onChange={handleIsPublicChange}
                />
              </FormItem>
            </FormRow>
            <FormRow>
              <FormItem label={formatMessage(newOrderMessages.selectionTypeLabel)}>
                <RadioGroup
                  isDisabled={!!from_primary_db}
                  direction='row'
                  name='selection_type'
                  value={selection_type ?? selectionTypeItems[0]?.value}
                  items={selectionTypeItems}
                  onChange={handleSelectionTypeChange}
                />
              </FormItem>
            </FormRow>
            <FormRow>
              <FormItem label={formatMessage(newOrderMessages.dataValidityLabel)}>
                <RadioGroup
                  isDisabled={!!from_primary_db}
                  direction='row'
                  name='order_type'
                  value={order_type ?? dataValidityItems[0]?.value}
                  items={dataValidityItems}
                  renderItem={
                    selection_type === OrderSelectionTypesEnum.CUSTOM
                      ? renderDataValidityCustomItem
                      : renderDataValidityPackageItem
                  }
                  onChange={handleDataValidityChange}
                />
              </FormItem>
            </FormRow>
          </div>
        )}
        <div className='dmx-orderPublishSetList--list'>
{filteredPublishsets.length > 0 ? (
            <ul>
              {filteredPublishsets.map((publishset) => (
                <li key={publishset.url}>
                  {<OrderPublishsetListItem publishset={publishset as any} />}
                </li>
              ))}
            </ul>
          ) : (
            publishsetList.queryState === QUERY_STATE.SUCCESS && (
              <InfoAlert>
                <FormattedMessage {...orderPublishsetListMessages.publishsetListNoFilteredData} />
              </InfoAlert>
            )
          )}
          {publishsetList.queryState === QUERY_STATE.WAITING && (
            <WaitingAlert>
              <FormattedMessage {...orderPublishsetListMessages.publishsetListStateWaiting} />
            </WaitingAlert>
          )}
          {publishsetList.queryState === QUERY_STATE.FAIL && (
            <DangerAlert>
              <FormattedMessage {...orderPublishsetListMessages.publishsetListStateFail} />
            </DangerAlert>
          )}
        </div>
      </div>

      <StickyFooter>
        <BtnBar align='right'>
          <CtaBtn
            icon={{ position: 'right', element: <SvgArrowRight /> }}
            onClick={onNextStepClick}
          >
            <FormattedMessage {...newOrderMessages.selectRegionNextBtn} />
          </CtaBtn>
        </BtnBar>
      </StickyFooter>
    </>
  );
};
